<template>
  <section class="border-t border-slate-200 pt-5">
    <div class="flex flex-wrap items-center justify-between gap-3">
      <div>
        <h3 class="text-sm font-semibold text-slate-900">附件</h3>
        <p class="mt-1 text-xs text-slate-500">支持拖拽上传和在线预览，附件会绑定到当前日报。</p>
      </div>
      <span v-if="uploadBusy" class="status-badge bg-blue-50 text-blue-700">处理中</span>
    </div>

    <div v-if="!readonly" class="mt-4">
      <FileUpload
        v-if="reportId && remainingUploadSlots > 0"
        :files="uploadFiles"
        module="work-report"
        bucket="private"
        :max-count="remainingUploadSlots"
        :max-size="maxSizeMb"
        :accept="uploadAccept"
        @update:files="handleUploadedFiles"
        @uploading-change="handleUploadingChange"
      />
      <div v-else class="rounded-lg border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-center text-sm text-slate-500">
        {{ reportId ? '附件数量已达上限' : '日报生成后可上传附件' }}
      </div>
    </div>

    <div v-if="items.length === 0" class="mt-4 rounded-lg border border-dashed border-slate-300 px-4 py-6 text-center text-sm text-slate-500">暂无附件</div>
    <FileAttachment
      v-else
      class="mt-4"
      :files="previewFiles"
      :loader="loadAttachmentFile"
      :removable="!readonly"
      :removing-id="deletingId"
      @remove="removeAttachment"
    />
  </section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import FileAttachment from '@/components/FileAttachment.vue'
import FileUpload from '@/components/FileUpload.vue'
import {
  deleteWorkReportAttachment,
  getWorkReportAttachmentDownloadUrl,
  registerWorkReportAttachment
} from '@/api/workReport'

const props = defineProps({
  reportId: { type: [Number, String], default: null },
  attachments: { type: Array, default: () => [] },
  readonly: { type: Boolean, default: false },
  maxCount: { type: Number, default: 20 },
  maxSizeMb: { type: Number, default: 20 },
  allowedMediaTypes: { type: [Array, String], default: '' },
  allowedExtensions: { type: [Array, String], default: '' }
})
const emit = defineEmits(['update:attachments', 'changed'])
const uploading = ref(false)
const deletingId = ref(null)
const localItems = ref([])
const uploadFiles = ref([])
const registeringUploads = ref(new Set())
const registeringCount = ref(0)
const items = computed(() => localItems.value)
const previewFiles = computed(() => items.value.map(normalizeAttachmentForPreview))
const uploadBusy = computed(() => uploading.value || registeringCount.value > 0)
const maxCount = computed(() => Number(props.maxCount) > 0 ? Number(props.maxCount) : 20)
const maxSizeMb = computed(() => Number(props.maxSizeMb) > 0 ? Number(props.maxSizeMb) : 20)
const remainingUploadSlots = computed(() => Math.max(0, maxCount.value - items.value.length))
const uploadAccept = computed(() => [
  ...normalizeAcceptList(props.allowedMediaTypes),
  ...normalizeAcceptList(props.allowedExtensions).map((item) => item.startsWith('.') || item.includes('/') ? item : `.${item}`)
].filter(Boolean).join(','))

watch(() => props.attachments, (value) => {
  localItems.value = Array.isArray(value) ? [...value] : []
}, { immediate: true, deep: true })

async function handleUploadedFiles(files) {
  uploadFiles.value = Array.isArray(files) ? files : []
  if (!props.reportId) return

  for (const file of uploadFiles.value) {
    await registerUploadedFile(file)
  }
}

async function registerUploadedFile(file) {
  const uploadKey = uploadIdentity(file)
  if (!uploadKey || registeringUploads.value.has(uploadKey)) return

  registeringUploads.value.add(uploadKey)
  registeringCount.value += 1
  try {
    const uploaded = file
    const objectKey = uploaded?.objectKey || uploaded?.key || uploaded?.fileKey || uploaded?.fileName
    if (!objectKey) {
      ElMessage.error('上传结果缺少文件标识，请重试')
      return
    }
    const attachment = await registerWorkReportAttachment(props.reportId, {
      objectKey,
      fileName: uploaded?.fileName || uploaded?.originalName || uploaded?.name,
      mediaType: uploaded?.mediaType || uploaded?.contentType || uploaded?.type || 'application/octet-stream',
      fileSize: uploaded?.fileSize || uploaded?.size
    })
    updateItems([...localItems.value, attachment])
    uploadFiles.value = uploadFiles.value.filter((item) => uploadIdentity(item) !== uploadKey)
    ElMessage.success('附件已上传')
  } catch {
    ElMessage.error('附件登记失败，请重试')
  } finally {
    registeringUploads.value.delete(uploadKey)
    registeringCount.value = Math.max(0, registeringCount.value - 1)
  }
}

function handleUploadingChange(value) {
  uploading.value = value
}

async function loadAttachmentFile(file) {
  const attachmentId = attachmentIdentity(file)
  if (!attachmentId) throw new Error('附件标识缺失')
  return getWorkReportAttachmentDownloadUrl(props.reportId, attachmentId)
}

async function removeAttachment(file) {
  const attachmentId = attachmentIdentity(file)
  if (!attachmentId) return

  deletingId.value = attachmentId
  try {
    await deleteWorkReportAttachment(props.reportId, attachmentId)
    updateItems(localItems.value.filter((item) => String(item.id) !== String(attachmentId)))
    ElMessage.success('附件已删除')
  } catch {
    ElMessage.error('附件删除失败，请重试')
  } finally {
    deletingId.value = null
  }
}

function updateItems(nextItems) {
  localItems.value = nextItems
  emit('update:attachments', nextItems)
  emit('changed', nextItems)
}

function normalizeAttachmentForPreview(attachment) {
  return {
    ...attachment,
    id: attachment.id,
    attachmentId: attachment.id,
    name: attachment.name || attachment.fileName || attachment.originalName,
    fileName: attachment.fileName || attachment.name || attachment.originalName,
    fileKey: attachment.objectKey || attachment.fileKey || String(attachment.id || ''),
    fileSize: attachment.fileSize || attachment.size,
    contentType: attachment.mediaType || attachment.contentType || attachment.type,
    previewMode: 'blob',
    raw: attachment
  }
}

function uploadIdentity(file) {
  return file?.uid || file?.objectKey || file?.key || file?.fileKey || file?.fileName || file?.name || ''
}

function attachmentIdentity(file) {
  if (!file || typeof file !== 'object') return null
  return file.attachmentId ?? file.id ?? file.raw?.id ?? null
}

function normalizeAcceptList(value) {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean)
  return String(value || '').split(',').map((item) => item.trim()).filter(Boolean)
}

// FileUpload performs storage upload: uploadFile(file, 'work-report', 'private').
</script>
